import { useEffect, useState } from 'react'
import {
  Box,
  Card,
  ExpandCollapse,
  FlexGrid,
  Spacer,
  StackView,
  ToggleSwitch,
  Tooltip,
  Typography,
} from '@telus-uds/components-web'
import { withLegalText } from '@/siteBuilder/utils/text/withLegalText'
import { ResponsiveProps } from '@/siteBuilder/utils/types/components'
import useBBViewport from '@/hooks/useBBViewport'
import useLocalStorage from '@/siteBuilder/utils/useLocalStorage'
import Icon from '../../core/Icon/Icon'
import Image from '../../core/Image/Image'
import { EqualHeightCardContainer, FlexContainer } from './Styles'

export const TOGGLE_ON = 'when_toggle_on'
export const TOGGLE_OFF = 'when_toggle_off'
export const DISPLAY_ALWAYS = 'display_always'
export const ON_TOGGLE_CONDITION = 'on'
export const OFF_TOGGLE_CONDITION = 'off'
export const INACTIVE_TOGGLE_CONDITION = 'inactive'
export const NA_TOGGLE_CONDITION = 'not_applicable'
export const FULL_BLEED = 'full_bleed'
export const BACKGROUND = 'background'

export type ToggleStatesProps = {
  term: boolean
  internet: boolean
  discount: boolean
}

type ToggleConditionsProps = {
  internet: string
  term: string
  discount: string
}

type ToggleContainerProps = {
  children: React.ReactNode
  paddingVertical?: number
  paddingHorizontal?: number
  flex?: number
  borderRadius?: number
}

export type ToggleCompProps = {
  toggleLabel: string
  toggleA11y: string
  toggleActive: boolean
  localStorageKey?: string
  tooltipText?: string
}

type ToggleProps = {
  toggleComp: ToggleCompProps
  toggleState: boolean
  setToggleState: (val: boolean) => void
  inactive?: boolean
  isMob?: boolean
  hideInactiveToggle?: boolean
}

type SavingsLabelProps = {
  termState: boolean
  discountState: boolean
  noSavingsText?: string
  oneSavingsText?: string
  twoSavingsText?: string
  savingsIcon?: string
}

type CardStyleOptions =
  | 'default'
  | 'grid'
  | 'light'
  | 'lighter'
  | 'lightest'
  | 'dark'
  | 'darker'
  | 'darkest'
  | 'feature'
  | 'feature2'
  | 'feature3'
  | 'feature4'

type ImgProps = {
  src: string
  width: number
  height: number
  alt?: string
}

export type CardProps = {
  components: React.ReactElement
  internetStateFilter: string
  termStateFilter: string
  discountStateFilter: string
  padding?: 'narrow' | 'intermediate' | 'compact' | 'custom'
  borderRadius?: 'default' | 'none' | 'small' | 'large'
  cardStyle?: CardStyleOptions
  img?: ImgProps
  imgStyle?: string
  isDecorative?: boolean
  imgAltText?: string
  fullBleedImagePosition?: ResponsiveProps<'none' | 'top' | 'bottom' | 'left' | 'right'>
  fullBleedContentAlignment?: ResponsiveProps<'start' | 'center' | 'end'>
  backgroundImageResizeOption?: ResponsiveProps<'center' | 'contain' | 'cover' | 'repeat' | 'stretch'>
}

type ComparisonCardsProps = {
  cards: CardProps[]
  internetToggle?: ToggleCompProps
  termToggle?: ToggleCompProps
  discountToggle?: ToggleCompProps
  toggleConditions?: ToggleConditionsProps[]
  hideInactiveToggle?: boolean
  noSavingsText?: string
  oneSavingsText?: string
  twoSavingsText?: string
  savingsIcon?: string
  isMobSavingsExpanded?: boolean
  xsColSpan?: number
  smColSpan?: number
  mdColSpan?: number
  lgColSpan?: number
  xlColSpan?: number
  equalHeightCards?: boolean
}

// Filters cards based on toggle states if they exist, otherwise returns all cards
export const filterCards = (
  cards: CardProps[],
  internetToggleExists: boolean,
  termToggleExists: boolean,
  discountToggleExists: boolean,
  internetToggleActive: boolean,
  termToggleActive: boolean,
  discountToggleActive: boolean
): CardProps[] => {
  if (internetToggleExists || termToggleExists || discountToggleExists) {
    return cards.filter((card) => {
      const internetFilter = card.internetStateFilter
      const termFilter = card.termStateFilter
      const discountFilter = card.discountStateFilter

      // Check if the card's filter condition matches the toggle state if toggle exists, otherwise default to true if toggle doesn't exist
      const internetMatch =
        !internetToggleExists ||
        (internetToggleActive && internetFilter === TOGGLE_ON) ||
        (!internetToggleActive && internetFilter === TOGGLE_OFF) ||
        internetFilter === DISPLAY_ALWAYS
      const termMatch =
        !termToggleExists ||
        (termToggleActive && termFilter === TOGGLE_ON) ||
        (!termToggleActive && termFilter === TOGGLE_OFF) ||
        termFilter === DISPLAY_ALWAYS
      const discountMatch =
        !discountToggleExists ||
        (discountToggleActive && discountFilter === TOGGLE_ON) ||
        (!discountToggleActive && discountFilter === TOGGLE_OFF) ||
        discountFilter === DISPLAY_ALWAYS

      // Show the card only if all conditions are met
      return termMatch && internetMatch && discountMatch
    })
  } else {
    return cards
  }
}

// If toggle conditions exist, check if the current toggle's condition shows inactive and if so check the other toggles and return true if state conditions are met
export const shouldToggleBeInactive = (
  toggleConditions: ToggleConditionsProps[],
  toggleStates: ToggleStatesProps,
  toggleType: keyof ToggleStatesProps
): boolean => {
  if (!toggleConditions?.length) return false

  // Fill in any undefined values with 'not_applicable'
  const filledToggleConditions = toggleConditions.map((condition) => {
    const filledCondition = { ...condition }
    Object.keys(filledCondition).forEach((key) => {
      if (filledCondition[key] === undefined) {
        filledCondition[key] = NA_TOGGLE_CONDITION
      }
    })
    return filledCondition
  })

  // Check if all toggles are inactive
  const allInactive = (Object.keys(toggleStates) as Array<keyof ToggleStatesProps>).every((type) =>
    filledToggleConditions.some((condition) => condition[type] === INACTIVE_TOGGLE_CONDITION)
  )

  if (allInactive) return true

  return filledToggleConditions.some((condition) => {
    // Check if current toggle is marked as inactive
    if (condition[toggleType] === INACTIVE_TOGGLE_CONDITION) {
      // Filter out other toggle types that don't have n/a conditions
      // If toggle exists and condition is n/a, that toggle's state is not considered
      const otherToggleTypes = (Object.keys(toggleStates) as Array<keyof ToggleStatesProps>).filter(
        (type) => type !== toggleType && condition[type] !== NA_TOGGLE_CONDITION
      )

      // If no other toggles have conditions, return false
      if (otherToggleTypes.length === 0) return false

      // Check other toggle states against their conditions and return true if a match is found
      return otherToggleTypes.every((type) => {
        const toggleState = toggleStates[type]
        const currentCondition = condition[type]

        // Skip state check if the toggle is inactive
        if (currentCondition === INACTIVE_TOGGLE_CONDITION) return true

        // Return true if the toggle state matches that toggle's condition
        return (
          (toggleState && currentCondition === ON_TOGGLE_CONDITION) ||
          (!toggleState && currentCondition === OFF_TOGGLE_CONDITION)
        )
      })
    }
    return false
  })
}

const Toggle = ({
  toggleComp,
  toggleState,
  setToggleState,
  inactive,
  isMob,
  hideInactiveToggle,
}: ToggleProps): JSX.Element => {
  const toggleLabel = toggleComp?.toggleLabel
  if (!toggleLabel) return null

  // Hide toggle instead of rendering it as inactive if conditions are met
  if (inactive && hideInactiveToggle) return null

  const testId = `${inactive ? 'inactive-' : ''}${isMob ? 'mob-' : ''}toggle-wrapper-testid`

  const toggleSwitch = (
    <ToggleSwitch
      testID="toggle-switch-testid"
      value={toggleState}
      onChange={setToggleState}
      accessibilityLabel={toggleComp?.toggleA11y}
      inactive={inactive}
    />
  )

  const labelText = (
    <Typography variant={{ size: 'small', colour: inactive ? 'light' : 'dark' }}>
      {withLegalText(toggleLabel)}
      {!isMob && toggleComp.tooltipText && <Tooltip inline content={toggleComp.tooltipText} />}
    </Typography>
  )

  if (isMob) {
    return (
      <StackView
        testID={testId}
        direction="row"
        space={2}
        tokens={{ alignItems: 'center', justifyContent: 'space-between' }}
      >
        <Box flex={1}>
          <StackView direction="row" space={2} tokens={{ alignItems: 'center' }}>
            {toggleSwitch}
            {labelText}
          </StackView>
        </Box>
        {toggleComp.tooltipText && <Tooltip content={toggleComp.tooltipText} />}
      </StackView>
    )
  }

  return (
    <FlexContainer data-testid={testId} height="100%">
      {toggleSwitch}
      {labelText}
    </FlexContainer>
  )
}

// Create savings label with icon and text that dynamically changes based on term and discount toggle states
const SavingsLabel = ({
  termState,
  discountState,
  noSavingsText,
  oneSavingsText,
  twoSavingsText,
  savingsIcon,
}: SavingsLabelProps): JSX.Element => {
  let text = ''

  // Get the number of active toggle states in the savings section (term and discount toggles only)
  const activeToggles = [termState, discountState].filter(Boolean).length
  // Get discount text based on the number of active toggle states
  if (activeToggles === 0) {
    text = noSavingsText
  }
  if (activeToggles === 1) {
    text = oneSavingsText
  }
  if (activeToggles === 2) {
    text = twoSavingsText
  }

  return (
    <FlexContainer data-testid="savings-label-testid">
      {savingsIcon && <Icon iconName={savingsIcon} color="brand" size="large" />}
      <Typography variant={{ size: 'medium', bold: true, colour: 'dark' }}>{withLegalText(text)}</Typography>
    </FlexContainer>
  )
}

// Custom box to hold toggle(s)
const ToggleContainer = ({
  children,
  paddingVertical = 2,
  paddingHorizontal = 3,
  flex,
  borderRadius = 12,
}: ToggleContainerProps): JSX.Element => (
  <Box
    vertical={paddingVertical}
    horizontal={paddingHorizontal}
    flex={flex}
    variant={{ background: 'lighter' }}
    tokens={{
      borderTopLeftRadius: borderRadius,
      borderTopRightRadius: borderRadius,
      borderBottomLeftRadius: borderRadius,
      borderBottomRightRadius: borderRadius,
    }}
  >
    {children}
  </Box>
)

const ComparisonCards = ({
  cards,
  internetToggle,
  termToggle,
  discountToggle,
  toggleConditions,
  hideInactiveToggle,
  noSavingsText,
  oneSavingsText,
  twoSavingsText,
  savingsIcon,
  isMobSavingsExpanded,
  xsColSpan = 12,
  smColSpan = undefined,
  mdColSpan = 6,
  lgColSpan = 4,
  xlColSpan = undefined,
  equalHeightCards = false,
}: ComparisonCardsProps) => {
  const viewport = useBBViewport()
  const initialToggleStates: ToggleStatesProps = {
    internet: internetToggle?.toggleActive ?? false,
    term: termToggle?.toggleActive ?? false,
    discount: discountToggle?.toggleActive ?? false,
  }
  const [filteredCards, setFilteredCards] = useState<CardProps[]>([])
  const [inactiveToggle, setInactiveToggle] = useState<ToggleStatesProps>({
    internet: shouldToggleBeInactive(toggleConditions, initialToggleStates, 'internet' as keyof ToggleStatesProps),
    term: shouldToggleBeInactive(toggleConditions, initialToggleStates, 'term' as keyof ToggleStatesProps),
    discount: shouldToggleBeInactive(toggleConditions, initialToggleStates, 'discount' as keyof ToggleStatesProps),
  })

  const [internetToggleActive, setInternetToggleActive] = useLocalStorage(
    internetToggle?.localStorageKey,
    internetToggle?.toggleActive
  )
  const [termToggleActive, setTermToggleActive] = useLocalStorage(termToggle?.localStorageKey, termToggle?.toggleActive)
  const [discountToggleActive, setDiscountToggleActive] = useLocalStorage(
    discountToggle?.localStorageKey,
    discountToggle?.toggleActive
  )

  // If equalHeightCards is true, wrap card and spacer in styled div that allows card to expand to the height of the tallest card in the row
  const CardContainer = ({ children }) => {
    if (equalHeightCards) {
      return <EqualHeightCardContainer data-testid="card-container-testid">{children}</EqualHeightCardContainer>
    }

    return <>{children}</>
  }

  const savingsLabel = (
    <SavingsLabel
      termState={termToggleActive}
      discountState={discountToggleActive}
      noSavingsText={noSavingsText}
      oneSavingsText={oneSavingsText}
      twoSavingsText={twoSavingsText}
      savingsIcon={savingsIcon}
    />
  )

  // Check if toggles should exist (created in Contentful)
  const internetToggleExists = !!internetToggle?.toggleLabel
  const termToggleExists = !!termToggle?.toggleLabel
  const discountToggleExists = !!discountToggle?.toggleLabel

  useEffect(() => {
    // Get current toggle states
    const states: ToggleStatesProps = {
      internet: internetToggleActive ?? false,
      term: termToggleActive ?? false,
      discount: discountToggleActive ?? false,
    }

    // Check if a toggle should be inactive based on current states and toggle conditions
    const newInactiveState = {
      internet: shouldToggleBeInactive(toggleConditions, states, 'internet' as keyof ToggleStatesProps),
      term: shouldToggleBeInactive(toggleConditions, states, 'term' as keyof ToggleStatesProps),
      discount: shouldToggleBeInactive(toggleConditions, states, 'discount' as keyof ToggleStatesProps),
    }

    // Update inactive states
    setInactiveToggle(newInactiveState)

    // Set toggle states to false when they become inactive
    if (newInactiveState.internet) {
      setInternetToggleActive(false)
    }
    if (newInactiveState.term) {
      setTermToggleActive(false)
    }
    if (newInactiveState.discount) {
      setDiscountToggleActive(false)
    }

    // Filter cards based on toggle states (if any)
    const newFilteredCards = filterCards(
      cards,
      internetToggleExists,
      termToggleExists,
      discountToggleExists,
      internetToggleActive,
      termToggleActive,
      discountToggleActive
    )
    setFilteredCards(newFilteredCards)
  }, [internetToggleActive, termToggleActive, discountToggleActive])

  return (
    <FlexGrid>
      {(internetToggleExists || termToggleExists || discountToggleExists) && (
        <FlexGrid.Row>
          <FlexGrid.Col>
            {['lg', 'xl'].includes(viewport) && (
              <StackView space={2} direction="row">
                {internetToggleExists && (
                  <ToggleContainer>
                    <Toggle
                      toggleComp={internetToggle}
                      toggleState={internetToggleActive}
                      setToggleState={setInternetToggleActive}
                      inactive={inactiveToggle.internet}
                      hideInactiveToggle={hideInactiveToggle}
                    />
                  </ToggleContainer>
                )}
                {(termToggleExists || discountToggleExists) && (
                  <ToggleContainer flex={1}>
                    <FlexContainer height="100%" justifyContent="space-between">
                      {savingsLabel}
                      <FlexContainer gap="16px">
                        <Toggle
                          toggleComp={termToggle}
                          toggleState={termToggleActive}
                          setToggleState={setTermToggleActive}
                          inactive={inactiveToggle.term}
                          hideInactiveToggle={hideInactiveToggle}
                        />
                        <Toggle
                          toggleComp={discountToggle}
                          toggleState={discountToggleActive}
                          setToggleState={setDiscountToggleActive}
                          inactive={inactiveToggle.discount}
                          hideInactiveToggle={hideInactiveToggle}
                        />
                      </FlexContainer>
                    </FlexContainer>
                  </ToggleContainer>
                )}
              </StackView>
            )}
            {['md', 'xs', 'sm'].includes(viewport) && (
              <StackView space={2}>
                {internetToggleExists && (
                  <ToggleContainer paddingVertical={3} borderRadius={6}>
                    <Toggle
                      toggleComp={internetToggle}
                      toggleState={internetToggleActive}
                      setToggleState={setInternetToggleActive}
                      inactive={inactiveToggle.internet}
                      isMob={true}
                      hideInactiveToggle={hideInactiveToggle}
                    />
                  </ToggleContainer>
                )}
                {(termToggleExists || discountToggleExists) && (
                  <ToggleContainer paddingVertical={0} paddingHorizontal={0} borderRadius={6}>
                    <ExpandCollapse initialOpen={isMobSavingsExpanded ? ['toggles'] : []}>
                      {(expandProps) => (
                        <ExpandCollapse.Panel
                          {...expandProps}
                          panelId="toggles"
                          controlTokens={{
                            iconPosition: 'right',
                            paddingRight: 16,
                            paddingLeft: 16,
                            verticalAlign: 'middle',
                            justifyContent: 'center',
                          }}
                          tokens={{ contentPaddingLeft: 16, contentPaddingRight: 13 }}
                          control={savingsLabel}
                        >
                          <Spacer space={2} />
                          <Toggle
                            toggleComp={termToggle}
                            toggleState={termToggleActive}
                            setToggleState={setTermToggleActive}
                            inactive={inactiveToggle.term}
                            isMob={true}
                            hideInactiveToggle={hideInactiveToggle}
                          />
                          {termToggleExists && discountToggleExists && <Spacer space={3} />}
                          <Toggle
                            toggleComp={discountToggle}
                            toggleState={discountToggleActive}
                            setToggleState={setDiscountToggleActive}
                            inactive={inactiveToggle.discount}
                            isMob={true}
                            hideInactiveToggle={hideInactiveToggle}
                          />
                        </ExpandCollapse.Panel>
                      )}
                    </ExpandCollapse>
                  </ToggleContainer>
                )}
              </StackView>
            )}
            <Spacer space={5} />
          </FlexGrid.Col>
        </FlexGrid.Row>
      )}
      <FlexGrid.Row>
        {filteredCards.length > 0 &&
          filteredCards.map((card, index) => {
            const imgStyle = card.imgStyle || FULL_BLEED

            if (
              typeof card.fullBleedImagePosition === 'object' &&
              Object.values(card.fullBleedImagePosition).some((value) => value === undefined)
            ) {
              // If it's an object and xs value is undefined, set it to 'bottom'
              // Other breakpoints will inherit xs value if undefined
              card.fullBleedImagePosition = {
                xs: card.fullBleedImagePosition.xs ?? 'bottom',
                sm: card.fullBleedImagePosition.sm,
                md: card.fullBleedImagePosition.md,
                lg: card.fullBleedImagePosition.lg,
                xl: card.fullBleedImagePosition.xl,
              }
            }

            return (
              <FlexGrid.Col
                key={`card-${index}`}
                xs={xsColSpan}
                sm={smColSpan}
                md={mdColSpan}
                lg={lgColSpan}
                xl={xlColSpan}
              >
                <CardContainer>
                  <Card
                    variant={{
                      padding: card.padding,
                      borderRadius: card.borderRadius,
                      style: card.cardStyle,
                    }}
                    fullBleedContent={
                      card.img?.src && imgStyle === FULL_BLEED
                        ? {
                            position: card.fullBleedImagePosition,
                            align: card.fullBleedContentAlignment,
                            content: (
                              <Image
                                src={card.img.src}
                                width={card.img.width}
                                height={card.img.height}
                                alt={card.imgAltText || card.img?.alt || ''}
                                decorative={card.isDecorative}
                              />
                            ),
                          }
                        : undefined
                    }
                    backgroundImage={
                      card.img?.src && imgStyle === BACKGROUND
                        ? {
                            src: card.img.src,
                            resizeMode: card.backgroundImageResizeOption,
                            alt: card.isDecorative ? '' : card.imgAltText || card.img?.alt || '',
                          }
                        : undefined
                    }
                  >
                    {card.components}
                  </Card>
                  <Spacer space={4} />
                </CardContainer>
              </FlexGrid.Col>
            )
          })}
      </FlexGrid.Row>
    </FlexGrid>
  )
}
export default ComparisonCards
